import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Truck, UserCheck, IndianRupee, Trash2, MoreVertical } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import EnhancedAddPartyModal from '../../components/modals/EnhancedAddPartyModal';
import DeleteConfirmationModal from '../../components/modals/DeleteConfirmationModal';
import ColumnChooser from '../../components/shared/ColumnChooser';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const SuppliersPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, supplier: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const defaultColumns = {
    name: true,
    mobile: true,
    email: false,
    gstin: true,
    city: false,
    balance: true,
    actions: true
  };
  const [visibleColumns, setVisibleColumns] = useState(defaultColumns);
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    mobile: '',
    email: '',
    gstin: '',
    city: '',
    balance: { min: '', max: '' }
  });

  useEffect(() => {
    if (businessId) {
      fetchSuppliers();
    }
  }, [businessId]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/parties?type=supplier');
      const data = response.data;
      setSuppliers(data.parties || data || []);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSupplier = () => {
    setEditingSupplier(null);
    setShowAddModal(true);
  };

  const handleViewSupplier = (supplier) => {
    navigate(`/business/${businessId}/suppliers/${supplier.party_id || supplier.id}`);
  };

  const handleModalClose = (shouldRefresh) => {
    setShowAddModal(false);
    setEditingSupplier(null);
    if (shouldRefresh) {
      fetchSuppliers();
    }
  };

  const handleDeleteClick = (supplier, e) => {
    e.stopPropagation();
    setDeleteModal({ open: true, supplier });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.supplier) return;
    
    setDeleteLoading(true);
    try {
      const supplierId = deleteModal.supplier.party_id || deleteModal.supplier.id;
      await api.delete(`/parties/${supplierId}`);
      toast.success('Supplier deleted successfully');
      setDeleteModal({ open: false, supplier: null });
      fetchSuppliers();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete supplier');
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredSuppliers = suppliers.filter(s => {
    // Column filters
    const matchesName = !columnFilters.name || 
      s.name?.toLowerCase().includes(columnFilters.name.toLowerCase());
    
    const matchesMobile = !columnFilters.mobile ||
      (s.phone || s.mobile || '').toLowerCase().includes(columnFilters.mobile.toLowerCase());
    
    const matchesEmail = !columnFilters.email || 
      s.email?.toLowerCase().includes(columnFilters.email.toLowerCase());
    
    const matchesGstin = !columnFilters.gstin || 
      s.gstin?.toLowerCase().includes(columnFilters.gstin.toLowerCase());
    
    const matchesCity = !columnFilters.city || 
      s.city?.toLowerCase().includes(columnFilters.city.toLowerCase());
    
    const balance = Math.abs(s.current_balance || s.balance || 0);
    const matchesBalance = (!columnFilters.balance?.min || balance >= parseFloat(columnFilters.balance.min)) &&
      (!columnFilters.balance?.max || balance <= parseFloat(columnFilters.balance.max));
    
    return matchesName && matchesMobile && matchesEmail && matchesGstin && matchesCity && matchesBalance;
  });

  const totalBalance = filteredSuppliers.reduce((sum, s) => sum + Math.abs(s.current_balance || s.balance || 0), 0);
  const activeCount = filteredSuppliers.filter(s => s.status === 'active' || !s.status).length;

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  return (
    <div className="h-full flex flex-col ">
      {/* Premium Header */}
      <ListPageHeader
        title="Suppliers"
        stats={[
          { icon: Truck, label: 'Total', value: filteredSuppliers.length, color: 'slate' },
          { icon: UserCheck, label: 'Active', value: activeCount, color: 'emerald' },
          { icon: IndianRupee, label: 'Payable', value: formatCompactCurrency(totalBalance), color: 'amber' },
        ]}
        ctaLabel="Add Supplier"
        onCtaClick={handleAddSupplier}
      />

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden">
            
            {/* Loading State */}
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-[3px] border-slate-200 border-t-orange-600 rounded-full animate-spin"></div>
                  <p className="text-[13px] text-slate-500 font-normal">Loading suppliers...</p>
                </div>
              </div>
            ) : filteredSuppliers.length === 0 ? (
              /* Empty State */
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                  <Truck className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-[15px] font-medium text-slate-600">No suppliers yet</p>
                <p className="text-[13px] text-slate-400 mt-1 mb-4">Add your first supplier to get started</p>
                <button
                  onClick={handleAddSupplier}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-[13px] font-medium"
                >
                  Add Supplier
                </button>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* TABLE HEADER */}
                <div className="flex-shrink-0">
                  <table className="tl-table">
                    <thead>
                      <tr>
                        {visibleColumns.name && (
                          <th className="tl-th tl-th-orange" style={{ width: '30%' }}>
                            <div className="flex items-center gap-1">
                              Supplier
                              <ColumnFilter 
                                type="text" 
                                value={columnFilters.name}
                                onChange={(val) => setColumnFilters({...columnFilters, name: val})}
                                placeholder="Filter name..."
                              />
                            </div>
                          </th>
                        )}
                        {visibleColumns.mobile && (
                          <th className="tl-th tl-th-orange" style={{ width: '15%' }}>
                            <div className="flex items-center gap-1">
                              Mobile
                              <ColumnFilter 
                                type="text" 
                                value={columnFilters.mobile}
                                onChange={(val) => setColumnFilters({...columnFilters, mobile: val})}
                                placeholder="Filter mobile..."
                              />
                            </div>
                          </th>
                        )}
                        {visibleColumns.email && (
                          <th className="tl-th tl-th-orange" style={{ width: '20%' }}>
                            <div className="flex items-center gap-1">
                              Email
                              <ColumnFilter 
                                type="text" 
                                value={columnFilters.email}
                                onChange={(val) => setColumnFilters({...columnFilters, email: val})}
                                placeholder="Filter email..."
                              />
                            </div>
                          </th>
                        )}
                        {visibleColumns.gstin && (
                          <th className="tl-th tl-th-orange" style={{ width: '20%' }}>
                            <div className="flex items-center gap-1">
                              GSTIN
                              <ColumnFilter 
                                type="text" 
                                value={columnFilters.gstin}
                                onChange={(val) => setColumnFilters({...columnFilters, gstin: val})}
                                placeholder="Filter GSTIN..."
                              />
                            </div>
                          </th>
                        )}
                        {visibleColumns.city && (
                          <th className="tl-th tl-th-orange" style={{ width: '15%' }}>
                            <div className="flex items-center gap-1">
                              City
                              <ColumnFilter 
                                type="text" 
                                value={columnFilters.city}
                                onChange={(val) => setColumnFilters({...columnFilters, city: val})}
                                placeholder="Filter city..."
                              />
                            </div>
                          </th>
                        )}
                        {visibleColumns.balance && (
                          <th className="tl-th tl-th-orange tl-th-right" style={{ width: '15%' }}>
                            <div className="flex items-center justify-end gap-1">
                              Balance
                              <ColumnFilter 
                                type="number" 
                                value={columnFilters.balance}
                                onChange={(val) => setColumnFilters({...columnFilters, balance: val})}
                              />
                            </div>
                          </th>
                        )}
                        {visibleColumns.actions && (
                          <th className="tl-th tl-th-orange" style={{ width: '50px' }}></th>
                        )}
                      </tr>
                    </thead>
                  </table>
                </div>

                {/* TABLE BODY - Scrollable */}
                <div className="flex-1 overflow-y-auto">
                  <table className="tl-table">
                    <tbody>
                      {filteredSuppliers.map((supplier, index) => {
                        const balance = supplier.balance || supplier.current_balance || 0;
                        return (
                          <tr 
                            key={supplier.party_id || supplier.id} 
                            className={`tl-tr tl-tr-hover-orange tl-tr-clickable ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                            onClick={() => handleViewSupplier(supplier)}
                          >
                            {visibleColumns.name && (
                              <td className="tl-td" style={{ width: '30%' }}>
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-[12px] font-medium text-orange-700">
                                      {supplier.name?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                  </div>
                                  <span className="tl-cell-primary truncate">{supplier.name}</span>
                                </div>
                              </td>
                            )}
                            {visibleColumns.mobile && (
                              <td className="tl-td tl-cell-regular" style={{ width: '15%' }}>
                                {supplier.phone || supplier.mobile || <span className="tl-cell-empty">--</span>}
                              </td>
                            )}
                            {visibleColumns.email && (
                              <td className="tl-td tl-cell-regular truncate" style={{ width: '20%' }}>
                                {supplier.email || <span className="tl-cell-empty">—</span>}
                              </td>
                            )}
                            {visibleColumns.gstin && (
                              <td className="tl-td" style={{ width: '20%' }}>
                                {supplier.gstin ? (
                                  <span className="tl-cell-mono">{supplier.gstin}</span>
                                ) : (
                                  <span className="tl-cell-empty">—</span>
                                )}
                              </td>
                            )}
                            {visibleColumns.city && (
                              <td className="tl-td tl-cell-regular" style={{ width: '15%' }}>
                                {supplier.city || <span className="tl-cell-empty">—</span>}
                              </td>
                            )}
                            {visibleColumns.balance && (
                              <td className="tl-td tl-td-right" style={{ width: '15%' }}>
                                {balance === 0 ? (
                                  <span className="tl-cell-amount text-gray-400">₹0</span>
                                ) : (
                                  <span className={`tl-cell-amount ${balance > 0 ? 'tl-cell-amount-negative' : 'tl-cell-amount-positive'}`}>
                                    ₹{Math.abs(balance).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                  </span>
                                )}
                              </td>
                            )}
                            {visibleColumns.actions && (
                              <td className="tl-td" style={{ width: '50px' }} onClick={(e) => e.stopPropagation()}>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button className="p-1 hover:bg-gray-100 rounded">
                                      <MoreVertical className="w-4 h-4 text-gray-500" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem 
                                      onClick={(e) => handleDeleteClick(supplier, e)}
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* TABLE FOOTER */}
                <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                  <p>
                    Showing <span className="font-medium text-slate-700">{filteredSuppliers.length}</span> of <span className="font-medium text-slate-700">{suppliers.length}</span> suppliers
                  </p>
                  <p>
                    Total Payable: <span className="font-medium text-slate-700">{formatCompactCurrency(totalBalance)}</span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <EnhancedAddPartyModal
          isOpen={showAddModal}
          onClose={handleModalClose}
          party={editingSupplier}
          partyType="supplier"
          businessId={businessId}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, supplier: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Supplier"
        itemName={deleteModal.supplier?.name || 'this supplier'}
        itemType="Supplier"
        loading={deleteLoading}
      />
    </div>
  );
};

export default SuppliersPage;
